import { useCreateEvent } from '@/features/events/hooks/useCreateEvent';
import { EventType as NewEventType } from '@/features/events/types/EventType';
import { Question } from '@/utils/api-objects/Question';
import { saveEvent } from '@/utils/apis/user-service';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseQuestionNavigationProps {
    questions: Question[] | undefined;
    onQuestionChange?: (question: Question) => void;
}

export const useQuestionNavigation = ({ questions, onQuestionChange }: UseQuestionNavigationProps) => {
    const navigate = useNavigate();
    const { sendEvent } = useCreateEvent();
    const { auth } = useAuthStore();
    const { userData } = useUserStore();
    const [index, setIndex] = useState(0);
    const [shouldShowCloseDialog, setShouldShowCloseDialog] = useState(false);

    const currentQuestion = questions?.[index];
    const maximumIndex = questions?.length ? questions.length - 1 : 0;

    const onBackButtonClick = async () => {
        setShouldShowCloseDialog(true);
        if (!userData.email || !currentQuestion) return;

        await saveEvent(
            userData.email,
            `exit_health_assessment_from_question_${currentQuestion.id}_button_clicked`,
            () => {},
            (error) => console.log(error),
            "health_assessment_questions_page"
        );

        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "close_button",
            event_metadata: { action: "close_health_assessment", question: currentQuestion.id }
        });
    };

    const onCloseDialogDismissClick = () => {
        navigate("/home");
    };

    const onCloseDialogConfirmClick = () => {
        setShouldShowCloseDialog(false);
    };

    const onBackClick = async () => {
        if (!userData.email || !auth.idToken || !currentQuestion) return;

        if (index === 0) {
            navigate("/health-assessment-home-page");
            await saveEvent(
                userData.email,
                `back_to_health_assessment_home_page_button_clicked`,
                () => {},
                (error) => console.log(error),
                "health_assessment_questions_page"
            );
        } else {
            setIndex(index - 1);
            if (onQuestionChange && questions?.[index - 1]) {
                onQuestionChange(questions[index - 1]);
            }
            await saveEvent(
                userData.email,
                `back_to_previous_question_from_question_${currentQuestion.id}_button_clicked`,
                () => {},
                (error) => console.log(error),
                "health_assessment_questions_page"
            );
        }

        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "back_button",
            event_metadata: { question: currentQuestion.id }
        });
    };

    const goToNextQuestion = () => {
        if (index < maximumIndex) {
            setIndex(index + 1);
            if (onQuestionChange && questions?.[index + 1]) {
                onQuestionChange(questions[index + 1]);
            }
        }
    };

    return {
        currentQuestion,
        index,
        maximumIndex,
        shouldShowCloseDialog,
        onBackButtonClick,
        onCloseDialogDismissClick,
        onCloseDialogConfirmClick,
        onBackClick,
        goToNextQuestion,
        progressPercentage: questions ? ((index + 1) / questions.length) * 100 : 0
    };
}; 